// File Location: src/services/leadGeneratorService.js

import {
  collection,
  addDoc,
  doc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  setDoc,
  runTransaction,
  getFirestore,
  serverTimestamp
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { db, storage, auth, secondaryAuth } from "./firebaseConfig";

// Lead Generator registration service
export const registerLeadGenerator = async (leadGeneratorData, documents) => {
  try {
    // Upload all documents first
    const documentUrls = {};
    const uploadPromises = [];

    for (const [docType, file] of Object.entries(documents)) {
      if (file) {
        const uploadPromise = uploadDocument(
          file,
          docType,
          leadGeneratorData.entityName
        );
        uploadPromises.push(
          uploadPromise.then((url) => {
            documentUrls[docType] = {
              url,
              fileName: file.name,
              size: file.size,
              uploadedAt: new Date(),
            };
          })
        );
      }
    }

    // Wait for all document uploads to complete
    await Promise.all(uploadPromises);

    // Reorganize multiple files back into proper structure
    const organizedDocuments = {};
    const otherProofFiles = [];
    
    Object.entries(documentUrls).forEach(([key, value]) => {
      if (key.startsWith('otherProof_')) {
        otherProofFiles.push(value);
      } else {
        organizedDocuments[key] = value;
      }
    });
    
    // Add organized other proof files if any exist
    if (otherProofFiles.length > 0) {
      organizedDocuments.otherProof = otherProofFiles;
    }

    // Prepare lead generator data for Firestore
    const leadGeneratorRecord = {
      // Entity Information
      entityType: leadGeneratorData.entityType,
      entityName: leadGeneratorData.entityName,
      cinNumber: leadGeneratorData.cinNumber,
      gstNumber: leadGeneratorData.gstNumber,
      address: leadGeneratorData.address,
      city: leadGeneratorData.city,
      state: leadGeneratorData.state,
      pincode: leadGeneratorData.pincode,
      phone: leadGeneratorData.phone,
      email: leadGeneratorData.email,
      website: leadGeneratorData.website || "",

      // Representative Information
      representative: {
        name: leadGeneratorData.representativeName,
        contact: leadGeneratorData.representativeContact,
        email: leadGeneratorData.representativeEmail,
      },

      // Official Contact
      officialEmail: leadGeneratorData.officialEmail,

      // Services Required
      servicesRequired: leadGeneratorData.servicesRequired,

      // Account Information
      accountEmail: leadGeneratorData.accountEmail,
      // Note: Password will be stored separately in Firebase Auth

      // Documents
      documents: organizedDocuments,

      // System fields
      status: "pending", // pending, approved, rejected
      applicationId: await generateApplicationId("SAM"),
      submittedAt: new Date(),
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: false, // Will be set to true when approved
      approvedBy: null,
      approvedAt: null,
      rejectionReason: null,
    };

    // Save to Firestore
    const docRef = await addDoc(
      collection(db, "leadGeneratorApplications"),
      leadGeneratorRecord
    );

    return {
      success: true,
      applicationId: leadGeneratorRecord.applicationId,
      docId: docRef.id,
      message: "Lead Generator registration submitted successfully!",
    };
  } catch (error) {
    console.error("Error registering lead generator:", error);
    return {
      success: false,
      error: error.message || "Failed to register lead generator",
    };
  }
};

// Upload document to Firebase Storage
export const uploadDocument = async (file, docType, entityName) => {
  try {
    const timestamp = Date.now();
    const uniqueToken = Math.random().toString(36).slice(2, 8);
    const fileName = `${docType}_${timestamp}_${uniqueToken}_${file.name}`;
    const storagePath = `leadGenerators/${entityName.replace(
      /\s+/g,
      "_"
    )}/documents/${fileName}`;

    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error("Error uploading document:", error);
    throw new Error(`Failed to upload ${docType}: ${error.message}`);
  }
};

// Generate unique application ID (uses same counter as hospital applications)
const generateApplicationId = async (prefix = "SAM") => {
  try {
    const db = getFirestore();
    // Use the same counter as hospital applications for consistent sequencing
    const counterRef = doc(db, 'counters', 'applicationId');
    
    // Use transaction to ensure atomicity - prevents duplicate IDs even with simultaneous submissions
    const newId = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      
      let currentCount = 1;
      if (counterDoc.exists()) {
        currentCount = (counterDoc.data().count || 0) + 1;
      }
      
      // Update the counter atomically
      transaction.set(counterRef, { count: currentCount, updatedAt: serverTimestamp() }, { merge: true });
      
      // Generate the application ID with padding (SAM00001, SAM00002, etc.)
      const paddedNumber = currentCount.toString().padStart(5, '0');
      return `${prefix}${paddedNumber}`;
    });
    
    return newId;
  } catch (error) {
    console.error('Error generating application ID:', error);
    // Fallback to timestamp-based ID if counter fails
    const timestamp = Date.now().toString().slice(-8);
    const random = Math.random().toString(36).substr(2, 4).toUpperCase();
    return `${prefix}${timestamp}${random}`;
  }
};

// Get all lead generator applications (for admin)
export const getLeadGeneratorApplications = async (status = null) => {
  try {
    let q = collection(db, "leadGeneratorApplications");

    if (status) {
      q = query(q, where("status", "==", status));
    }

    q = query(q, orderBy("submittedAt", "desc"));

    const querySnapshot = await getDocs(q);
    const applications = [];

    querySnapshot.forEach((doc) => {
      applications.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return {
      success: true,
      applications,
    };
  } catch (error) {
    console.error("Error fetching lead generator applications:", error);
    return {
      success: false,
      error: error.message,
      applications: [],
    };
  }
};

// Get single lead generator application
export const getLeadGeneratorApplication = async (applicationId) => {
  try {
    const q = query(
      collection(db, "leadGeneratorApplications"),
      where("applicationId", "==", applicationId)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return {
        success: false,
        error: "Application not found",
      };
    }

    const doc = querySnapshot.docs[0];
    return {
      success: true,
      application: {
        id: doc.id,
        ...doc.data(),
      },
    };
  } catch (error) {
    console.error("Error fetching lead generator application:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Approve lead generator application
export const approveLeadGeneratorApplication = async (
  applicationId,
  approvedBy,
  password,
  approvedServices = []
) => {
  try {
    // Get application data
    const appResult = await getLeadGeneratorApplication(applicationId);
    if (!appResult.success) {
      throw new Error("Application not found");
    }

    const application = appResult.application;

    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(
      secondaryAuth,
      application.accountEmail,
      password
    );

    // Create user document in users collection
    const userData = {
      uid: userCredential.user.uid,
      email: application.accountEmail,
      role: "LeadGenerator",
      leadGeneratorId: application.id,
      entityName: application.entityName,
      isActive: true,
      createdAt: new Date(),
      profile: {
        name: application.representative.name,
        phone: application.representative.contact,
        email: application.representative.email,
      },
      servicesAccess: approvedServices, // Use approved services instead of all requested
    };

    await setDoc(doc(db, "users", userCredential.user.uid), userData);

    // Update lead generator application status
    const leadGeneratorRef = doc(db, "leadGeneratorApplications", application.id);
    await updateDoc(leadGeneratorRef, {
      status: "approved",
      isActive: true,
      approvedBy: approvedBy,
      approvedAt: new Date(),
      updatedAt: new Date(),
      authUserId: userCredential.user.uid,
      approvedServices: approvedServices, // Store approved services
      isBlocked: false, // Initialize as not blocked
    });

    const result = {
      success: true,
      message: "Lead Generator approved successfully",
      userId: userCredential.user.uid,
    };
    try {
      if (secondaryAuth.currentUser) {
        await secondaryAuth.signOut();
      }
    } catch {}
    return result;
  } catch (error) {
    console.error("Error approving lead generator:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};


export const updateLeadGeneratorServices = async (
  leadGeneratorId,
  updatedBy,
  services
) => {
  try {
    // Update lead generator application
    const leadGeneratorRef = doc(db, "leadGeneratorApplications", leadGeneratorId);
    await updateDoc(leadGeneratorRef, {
      approvedServices: services,
      updatedBy: updatedBy,
      updatedAt: new Date(),
    });

    // Also update the user's services access
    const userQuery = query(
      collection(db, "users"),
      where("leadGeneratorId", "==", leadGeneratorId)
    );
    
    const userSnapshot = await getDocs(userQuery);
    if (!userSnapshot.empty) {
      const userDoc = userSnapshot.docs[0];
      const userRef = doc(db, "users", userDoc.id);
      await updateDoc(userRef, {
        servicesAccess: services,
        updatedAt: new Date(),
      });
    }

    // Log the activity
    await addDoc(collection(db, "adminActivities"), {
      action: "services_updated",
      leadGeneratorId: leadGeneratorId,
      updatedBy: updatedBy,
      services: services,
      timestamp: new Date(),
    });

    return {
      success: true,
      message: "Services updated successfully",
    };
  } catch (error) {
    console.error("Error updating services:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Block lead generator account
export const blockLeadGenerator = async (leadGeneratorId, blockedBy, reason) => {
  try {
    // Update lead generator application
    const leadGeneratorRef = doc(db, "leadGeneratorApplications", leadGeneratorId);
    await updateDoc(leadGeneratorRef, {
      isBlocked: true,
      blockedBy: blockedBy,
      blockedAt: new Date(),
      blockReason: reason,
      updatedAt: new Date(),
    });

    // Also update the user's status
    const userQuery = query(
      collection(db, "users"),
      where("leadGeneratorId", "==", leadGeneratorId)
    );
    
    const userSnapshot = await getDocs(userQuery);
    if (!userSnapshot.empty) {
      const userDoc = userSnapshot.docs[0];
      const userRef = doc(db, "users", userDoc.id);
      await updateDoc(userRef, {
        isBlocked: true,
        blockedBy: blockedBy,
        blockedAt: new Date(),
        blockReason: reason,
        updatedAt: new Date(),
      });
    }

    // Log the activity
    await addDoc(collection(db, "adminActivities"), {
      action: "account_blocked",
      leadGeneratorId: leadGeneratorId,
      blockedBy: blockedBy,
      reason: reason,
      timestamp: new Date(),
    });

    return {
      success: true,
      message: "Account blocked successfully",
    };
  } catch (error) {
    console.error("Error blocking account:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Unblock lead generator account
export const unblockLeadGenerator = async (leadGeneratorId, unblockedBy) => {
  try {
    // Update lead generator application
    const leadGeneratorRef = doc(db, "leadGeneratorApplications", leadGeneratorId);
    await updateDoc(leadGeneratorRef, {
      isBlocked: false,
      unblockedBy: unblockedBy,
      unblockedAt: new Date(),
      blockReason: null, // Clear the block reason
      updatedAt: new Date(),
    });

    // Also update the user's status
    const userQuery = query(
      collection(db, "users"),
      where("leadGeneratorId", "==", leadGeneratorId)
    );
    
    const userSnapshot = await getDocs(userQuery);
    if (!userSnapshot.empty) {
      const userDoc = userSnapshot.docs[0];
      const userRef = doc(db, "users", userDoc.id);
      await updateDoc(userRef, {
        isBlocked: false,
        unblockedBy: unblockedBy,
        unblockedAt: new Date(),
        blockReason: null,
        updatedAt: new Date(),
      });
    }

    // Log the activity
    await addDoc(collection(db, "adminActivities"), {
      action: "account_unblocked",
      leadGeneratorId: leadGeneratorId,
      unblockedBy: unblockedBy,
      timestamp: new Date(),
    });

    return {
      success: true,
      message: "Account unblocked successfully",
    };
  } catch (error) {
    console.error("Error unblocking account:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Get lead generator activity log
export const getLeadGeneratorActivityLog = async (leadGeneratorId) => {
  try {
    const activitiesQuery = query(
      collection(db, "adminActivities"),
      where("leadGeneratorId", "==", leadGeneratorId),
      orderBy("timestamp", "desc")
    );

    const querySnapshot = await getDocs(activitiesQuery);
    const activities = [];

    querySnapshot.forEach((doc) => {
      activities.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return {
      success: true,
      activities,
    };
  } catch (error) {
    console.error("Error fetching activity log:", error);
    return {
      success: false,
      error: error.message,
      activities: [],
    };
  }
};

// Bulk update lead generator services
export const bulkUpdateLeadGeneratorServices = async (updates) => {
  try {
    const batch = writeBatch(db);
    
    updates.forEach(({ leadGeneratorId, services, updatedBy }) => {
      const leadGeneratorRef = doc(db, "leadGeneratorApplications", leadGeneratorId);
      batch.update(leadGeneratorRef, {
        approvedServices: services,
        updatedBy: updatedBy,
        updatedAt: new Date(),
      });
    });

    await batch.commit();

    return {
      success: true,
      message: "Bulk services update completed",
    };
  } catch (error) {
    console.error("Error in bulk update:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Get service usage statistics
export const getServiceUsageStats = async () => {
  try {
    const leadGeneratorsQuery = query(
      collection(db, "leadGeneratorApplications"),
      where("status", "==", "approved"),
      where("isActive", "==", true)
    );

    const querySnapshot = await getDocs(leadGeneratorsQuery);
    const serviceStats = {};

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.approvedServices && Array.isArray(data.approvedServices)) {
        data.approvedServices.forEach((service) => {
          serviceStats[service] = (serviceStats[service] || 0) + 1;
        });
      }
    });

    return {
      success: true,
      stats: serviceStats,
      totalApprovedLeadGenerators: querySnapshot.size,
    };
  } catch (error) {
    console.error("Error fetching service usage stats:", error);
    return {
      success: false,
      error: error.message,
      stats: {},
      totalApprovedLeadGenerators: 0,
    };
  }
};

// Send notification to lead generator
export const sendNotificationToLeadGenerator = async (
  leadGeneratorId,
  message,
  type = "info"
) => {
  try {
    // Get lead generator details
    const leadGeneratorDoc = await getDoc(
      doc(db, "leadGeneratorApplications", leadGeneratorId)
    );
    
    if (!leadGeneratorDoc.exists()) {
      throw new Error("Lead Generator not found");
    }

    const leadGeneratorData = leadGeneratorDoc.data();

    // Create notification record
    const notification = {
      leadGeneratorId: leadGeneratorId,
      entityName: leadGeneratorData.entityName,
      message: message,
      type: type, // info, warning, success, error
      isRead: false,
      createdAt: new Date(),
      recipientEmail: leadGeneratorData.email,
    };

    await addDoc(collection(db, "notifications"), notification);

    // Here you could also integrate with email service
    // await sendEmailNotification(leadGeneratorData.email, message);

    return {
      success: true,
      message: "Notification sent successfully",
    };
  } catch (error) {
    console.error("Error sending notification:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Reject lead generator application
export const rejectLeadGeneratorApplication = async (
  applicationId,
  rejectedBy,
  reason
) => {
  try {
    const appResult = await getLeadGeneratorApplication(applicationId);
    if (!appResult.success) {
      throw new Error("Application not found");
    }

    const leadGeneratorRef = doc(
      db,
      "leadGeneratorApplications",
      appResult.application.id
    );
    await updateDoc(leadGeneratorRef, {
      status: "rejected",
      rejectedBy: rejectedBy,
      rejectedAt: new Date(),
      rejectionReason: reason,
      updatedAt: new Date(),
    });

    return {
      success: true,
      message: "Lead Generator application rejected",
    };
  } catch (error) {
    console.error("Error rejecting lead generator:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Request additional documents
export const requestAdditionalDocuments = async (
  applicationId,
  requestedBy,
  message,
  documents
) => {
  try {
    // First get the application
    const applicationResult = await getLeadGeneratorApplication(applicationId);
    if (!applicationResult.success) {
      return applicationResult;
    }

    const application = applicationResult.application;

    // Create a document request record
    const requestData = {
      applicationId: applicationId,
      leadGeneratorId: application.id,
      entityName: application.entityName,
      requestedBy: requestedBy,
      message: message,
      requestedDocuments: documents,
      status: "pending",
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Add to document requests collection
    await addDoc(collection(db, "documentRequests"), requestData);

    // Update application with request status
    const applicationDoc = doc(db, "leadGeneratorApplications", application.id);
    await updateDoc(applicationDoc, {
      hasDocumentRequest: true,
      lastDocumentRequestAt: new Date(),
      updatedAt: new Date(),
    });

    return {
      success: true,
      message: "Document request sent successfully",
    };
  } catch (error) {
    console.error("Error requesting additional documents:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Get approved lead generators (for dropdowns, etc.)
export const getApprovedLeadGenerators = async () => {
  try {
    const q = query(
      collection(db, "leadGeneratorApplications"),
      where("status", "==", "approved"),
      where("isActive", "==", true),
      orderBy("entityName")
    );

    const querySnapshot = await getDocs(q);
    const leadGenerators = [];

    querySnapshot.forEach((doc) => {
      leadGenerators.push({
        id: doc.id,
        entityName: doc.data().entityName,
        city: doc.data().city,
        state: doc.data().state,
        servicesRequired: doc.data().servicesRequired,
        cinNumber: doc.data().cinNumber,
        leadGeneratorId: doc.data().leadGeneratorId,
      });
    });

    return {
      success: true,
      leadGenerators,
    };
  } catch (error) {
    console.error("Error fetching approved lead generators:", error);
    return {
      success: false,
      error: error.message,
      leadGenerators: [],
    };
  }
};

// Update lead generator profile
export const updateLeadGeneratorProfile = async (leadGeneratorId, updateData) => {
  try {
    const leadGeneratorRef = doc(db, "leadGeneratorApplications", leadGeneratorId);
    await updateDoc(leadGeneratorRef, {
      ...updateData,
      updatedAt: new Date(),
    });

    return {
      success: true,
      message: "Lead Generator profile updated successfully",
    };
  } catch (error) {
    console.error("Error updating lead generator profile:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Deactivate lead generator
export const deactivateLeadGenerator = async (leadGeneratorId, deactivatedBy, reason) => {
  try {
    const leadGeneratorDoc = doc(db, "leadGeneratorApplications", leadGeneratorId);
    await updateDoc(leadGeneratorDoc, {
      isActive: false,
      deactivatedBy: deactivatedBy,
      deactivatedAt: new Date(),
      deactivationReason: reason,
      updatedAt: new Date(),
    });

    return {
      success: true,
      message: "Lead Generator deactivated successfully",
    };
  } catch (error) {
    console.error("Error deactivating lead generator:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Get lead generator statistics
export const getLeadGeneratorStats = async () => {
  try {
    const leadGeneratorApplicationsRef = collection(db, "leadGeneratorApplications");

    // Get lead generator application counts
    const totalQuery = await getDocs(leadGeneratorApplicationsRef);
    const pendingQuery = await getDocs(
      query(leadGeneratorApplicationsRef, where("status", "==", "pending"))
    );
    const approvedQuery = await getDocs(
      query(leadGeneratorApplicationsRef, where("status", "==", "approved"))
    );
    const rejectedQuery = await getDocs(
      query(leadGeneratorApplicationsRef, where("status", "==", "rejected"))
    );

    // Calculate this month applications
    const currentMonth = new Date();
    currentMonth.setDate(1);
    currentMonth.setHours(0, 0, 0, 0);

    const thisMonthQuery = await getDocs(
      query(leadGeneratorApplicationsRef, where("submittedAt", ">=", currentMonth))
    );

    return {
      success: true,
      stats: {
        totalApplications: totalQuery.size,
        pendingApplications: pendingQuery.size,
        approvedLeadGenerators: approvedQuery.size,
        rejectedApplications: rejectedQuery.size,
        thisMonthApplications: thisMonthQuery.size,
      },
    };
  } catch (error) {
    console.error("Error fetching lead generator stats:", error);
    return {
      success: false,
      error: error.message,
      stats: {
        totalApplications: 0,
        pendingApplications: 0,
        approvedLeadGenerators: 0,
        rejectedApplications: 0,
        thisMonthApplications: 0,
      },
    };
  }
};

// Delete document from storage
export const deleteDocument = async (documentUrl) => {
  try {
    const storageRef = ref(storage, documentUrl);
    await deleteObject(storageRef);
    return { success: true };
  } catch (error) {
    console.error("Error deleting document:", error);
    return { success: false, error: error.message };
  }
};

// Get recent activities for admin dashboard
export const getRecentActivities = async (limit = 10) => {
  try {
    const leadGeneratorQuery = query(
      collection(db, "leadGeneratorApplications"),
      orderBy("updatedAt", "desc"),
      limit(limit)
    );

    const querySnapshot = await getDocs(leadGeneratorQuery);
    const activities = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      activities.push({
        id: doc.id,
        type: "leadGenerator",
        action: data.status,
        entityName: data.entityName,
        timestamp: data.updatedAt,
        details: data,
      });
    });

    return {
      success: true,
      activities,
    };
  } catch (error) {
    console.error("Error fetching recent activities:", error);
    return {
      success: false,
      error: error.message,
      activities: [],
    };
  }
};

// Get lead generator data by leadGeneratorId
export const getLeadGeneratorById = async (leadGeneratorId) => {
  try {
    if (!leadGeneratorId) {
      return {
        success: false,
        error: "Lead generator ID is required",
      };
    }

    console.log("Fetching lead generator with ID:", leadGeneratorId);

    // First try leadGenerators collection
    let docRef = doc(db, "leadGenerators", leadGeneratorId);
    let docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      console.log("Found lead generator in leadGenerators collection:", docSnap.id);
      return {
        success: true,
        leadGenerator: {
          id: docSnap.id,
          ...docSnap.data(),
        },
      };
    }

    // If not found, try leadGeneratorApplications collection
    console.log("Not found in leadGenerators, trying leadGeneratorApplications...");
    docRef = doc(db, "leadGeneratorApplications", leadGeneratorId);
    docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      console.log("Found lead generator in leadGeneratorApplications collection:", docSnap.id);
      return {
        success: true,
        leadGenerator: {
          id: docSnap.id,
          ...docSnap.data(),
        },
      };
    }

    console.log("No lead generator found with ID:", leadGeneratorId);
    return {
      success: false,
      error: "Lead generator not found with ID: " + leadGeneratorId,
    };
  } catch (error) {
    console.error("Error fetching lead generator by ID:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Get lead generator logo URL
export const getLeadGeneratorLogoUrl = async (leadGeneratorId) => {
  try {
    const result = await getLeadGeneratorById(leadGeneratorId);
    if (!result.success) {
      return {
        success: false,
        error: result.error,
      };
    }

    // Get logo URL from documents.entityLogo.url field
    const logoUrl = result.leadGenerator?.documents?.entityLogo?.url;
    
    return {
      success: true,
      url: logoUrl,
    };
  } catch (error) {
    console.error("Error fetching lead generator logo URL:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};